import React, { useEffect, useMemo, useState } from 'react'
import {
  DataTable,
  DEFAULT_PAGINATION_PAGE_SIZE_OPTIONS,
  Modal,
  Search,
  type DataTableColumn,
  type DataTablePaginationProps,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { IDentalChart, IPerioChart } from '@dataTypes/chartingFeature.types'
import { INote } from '@dataTypes/note.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import {
  setCreateDentalChart,
  setCreatePerioChart,
  setPreferCreateDentalChart,
  setPreferCreatePerioChart,
  setSelectedDentalChart,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import { EmptyDentalChart, EmptyPerioChart } from '@constants/Charting'
import {
  compareChartHistorySortValues,
  handleChartHistoryTableSort,
} from '@utils/charting-history-table-sort.util'

type ExistingChart = IDentalChart | IPerioChart

type ExistingChartRow = DataTableRow & {
  id: string
  date: string
  dateSortKey: number
  title: string
  provider_name: string
  notesText: string
  selected: boolean
}

type ExistingChartSortColumn = 'date' | 'title' | 'provider_name' | 'notesText'

const PAGE_SIZE = DEFAULT_PAGINATION_PAGE_SIZE_OPTIONS[0]

interface CreateExistingChartModalProps {
  show: boolean
  onHide: () => void
  chartType: 'dental' | 'perio'
  searchTerm: string
  onSearchChange: (term: string) => void
}

const CreateExistingChartModal: React.FC<CreateExistingChartModalProps> = ({
  show,
  onHide,
  chartType,
  searchTerm,
  onSearchChange,
}) => {
  const titleMap = {
    dental: 'Dental',
    perio: 'Perio',
  }

  const columns = useMemo(
    (): DataTableColumn<ExistingChartRow>[] => [
      {
        id: 'date',
        label: 'Date',
        width: 100,
        sortable: true,
        renderCell: (row): React.ReactNode => (
          <button
            type="button"
            className="create-existing-chart-row-select"
            aria-label={`Select ${row.title} chart from ${row.date}`}
            aria-pressed={row.selected}
          >
            {row.date}
          </button>
        ),
      },
      {
        id: 'title',
        label: 'Chart name',
        width: 200,
        sortable: true,
      },
      {
        id: 'provider_name',
        label: 'Provider',
        width: 200,
        sortable: true,
      },
      {
        id: 'notesText',
        label: 'Notes',
        width: 250,
        sortable: true,
        renderCell: (row): React.ReactNode => row.notesText || '-',
      },
    ],
    [],
  )
  const dispatch = useAppDispatch()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const allChartHistory = useAppSelector((state) =>
    chartType === 'dental'
      ? state.charting.dentalCharts
      : state.charting.perioCharts,
  )

  // Filter charts to only show those belonging to the current patient
  const chartHistory =
    selectedPatient && !chartingLoading
      ? (allChartHistory as (IDentalChart | IPerioChart)[]).filter(
          (chart) => chart.patient_id === selectedPatient.patient_id,
        )
      : []

  // Filter chart history based on search term
  const filteredChartHistory = searchTerm.trim()
    ? chartType === 'dental'
      ? (chartHistory as IDentalChart[]).filter((chart: IDentalChart) => {
          const searchLower = searchTerm.toLowerCase()
          const notesText =
            chart.notes
              ?.map((note: INote) => `${note.title} ${note.body}`)
              .join(' ') || ''
          return (
            chart.title?.toLowerCase().includes(searchLower) ||
            notesText.toLowerCase().includes(searchLower) ||
            chart.date_time_created_at?.toLowerCase().includes(searchLower) ||
            chart.provider?.first_name?.toLowerCase().includes(searchLower) ||
            chart.provider?.last_name?.toLowerCase().includes(searchLower)
          )
        })
      : (chartHistory as IPerioChart[]).filter((chart: IPerioChart) => {
          const searchLower = searchTerm.toLowerCase()
          const notesText =
            chart.notes
              ?.map((note: INote) => `${note.title} ${note.body}`)
              .join(' ') || ''
          return (
            chart.title?.toLowerCase().includes(searchLower) ||
            notesText.toLowerCase().includes(searchLower) ||
            chart.date_time_created_at?.toLowerCase().includes(searchLower) ||
            chart.provider?.first_name?.toLowerCase().includes(searchLower) ||
            chart.provider?.last_name?.toLowerCase().includes(searchLower)
          )
        })
    : chartHistory

  const [selectedChart, setSelectedChart] = useState<
    IPerioChart | IDentalChart | null
  >(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortColumnId, setSortColumnId] =
    useState<ExistingChartSortColumn | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const chartNoun = chartType === 'dental' ? 'dental charts' : 'perio charts'
  const hasNoRecords = !chartingLoading && chartHistory.length === 0
  const showEmptyState = !chartingLoading && filteredChartHistory.length === 0
  const emptyDescription = !selectedPatient
    ? 'Select a patient to view their charts.'
    : hasNoRecords
      ? `You don't have any ${chartNoun} on record yet.`
      : `No ${chartNoun} match your search.`

  useEffect(() => {
    if (showEmptyState) {
      setSelectedChart(null)
    }
  }, [showEmptyState])

  useEffect(() => {
    setSelectedChart(null)
    setCurrentPage(1)
    setSortColumnId(null)
    setSortDirection('asc')
  }, [chartType, show])

  const rows = useMemo((): ExistingChartRow[] => {
    const mappedRows = filteredChartHistory.map((chart: ExistingChart) => ({
      id: chart.chart_id,
      date: new Date(chart.date_time_created_at ?? '').toLocaleDateString(),
      dateSortKey: new Date(chart.date_time_created_at ?? '').getTime() || 0,
      title: chart.title ?? '',
      provider_name: formatProviderDisplayName(chart.provider),
      notesText: (chart.notes ?? []).map((note: INote) => note.body).join(', '),
      selected: chart.chart_id === selectedChart?.chart_id,
    }))
    if (!sortColumnId) {
      return mappedRows
    }
    return mappedRows.sort((left, right) => {
      const leftValue =
        sortColumnId === 'date' ? left.dateSortKey : left[sortColumnId]
      const rightValue =
        sortColumnId === 'date' ? right.dateSortKey : right[sortColumnId]
      return compareChartHistorySortValues(
        leftValue as string | number,
        rightValue as string | number,
        sortDirection,
      )
    })
  }, [
    filteredChartHistory,
    selectedChart?.chart_id,
    sortColumnId,
    sortDirection,
  ])

  const chartById = useMemo(
    () =>
      new Map(
        filteredChartHistory.map((chart: ExistingChart) => [
          chart.chart_id,
          chart,
        ]),
      ),
    [filteredChartHistory],
  )

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const pagedRows = rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  )

  const handleRowSelection = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    if (chartingLoading || showEmptyState) {
      return
    }
    const rowElement = (event.target as HTMLElement).closest(
      'tr.ds-data-table__row',
    )
    if (!rowElement || !event.currentTarget.contains(rowElement)) {
      return
    }
    const siblingRows = Array.from(
      rowElement.parentElement?.children ?? [],
    ).filter((element) => element.classList.contains('ds-data-table__row'))
    const row = pagedRows[siblingRows.indexOf(rowElement)]
    if (row) {
      setSelectedChart(chartById.get(row.id) ?? null)
    }
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const handleSort = (columnId: string): void => {
    handleChartHistoryTableSort(
      columnId,
      sortColumnId,
      sortDirection,
      (value) => setSortColumnId(value as ExistingChartSortColumn | null),
      setSortDirection,
      setCurrentPage,
    )
  }
  const handleCreateChart = (): void => {
    if (!selectedChart) {
      return
    }
    if (chartType === 'dental') {
      dispatch(setSelectedDentalChart(null))
      dispatch(setPreferCreateDentalChart(true))
      dispatch(
        setCreateDentalChart({
          ...EmptyDentalChart,
          patient_id: selectedChart.patient_id,
          dental_data: (selectedChart as IDentalChart).dental_data,
        }),
      )
    } else {
      dispatch(setSelectedPerioChart(null))
      dispatch(setPreferCreatePerioChart(true))
      dispatch(
        setCreatePerioChart({
          ...EmptyPerioChart,
          patient_id: selectedChart.patient_id,
          perio_data: (selectedChart as IPerioChart).perio_data,
        }),
      )
    }
    onHide()
  }

  return (
    <Modal
      open={show}
      onClose={onHide}
      size="lg"
      className="create-existing-chart-modal"
      title={`Create from existing ${chartType} chart`}
      bodyClassName="overflow-auto"
      style={{ maxHeight: '75vh' }}
      primaryLabel="Create chart"
      secondaryLabel="Cancel"
      primaryDisabled={!selectedChart}
      onPrimaryClick={handleCreateChart}
      onSecondaryClick={onHide}
    >
      <div>
        <div className="create-existing-chart-toolbar">
          <span className="create-existing-chart-toolbar__title">
            {titleMap[chartType]} charting history
          </span>
          <Search
            className="create-existing-chart-search"
            value={searchTerm}
            placeholder={`Search ${chartType} charts`}
            ariaLabel={`Search ${chartType} charts`}
            showFilter={false}
            onChange={onSearchChange}
            onClear={() => onSearchChange('')}
          />
        </div>
        <div className="charting-history" onClick={handleRowSelection}>
          <DataTable<ExistingChartRow>
            columns={columns}
            rows={pagedRows}
            state={
              chartingLoading ? 'loading' : showEmptyState ? 'empty' : 'default'
            }
            emptyState={{
              title: `No ${chartNoun} found`,
              description: emptyDescription,
            }}
            className="charting-history-table"
            selectedRowIds={selectedChart ? [selectedChart.chart_id] : []}
            showActions={false}
            sortColumnId={sortColumnId ?? undefined}
            sortDirection={sortColumnId ? sortDirection : undefined}
            onSort={handleSort}
            pagination={
              chartingLoading || showEmptyState
                ? undefined
                : ({
                    currentPage,
                    totalPages,
                    onPageChange: setCurrentPage,
                  } as DataTablePaginationProps)
            }
          />
        </div>
      </div>
    </Modal>
  )
}

export default CreateExistingChartModal
